import { DateTime } from '@ksyos/date-time';
import type { IntoxicationCustomControlData } from '@common/validation/customControls/osas/intoxicationSchemas';
import type { WatchPatSleepStudyPdfData } from '@common/validation/customControls/osas/osasCloudPatExaminationSchema';
import type {
    OsasConsultData,
    OsasConsultExaminationData,
    OsasNoChosenTreatmentData,
    OsasNoOsaPolicyData,
    OsasYesChosenTreatmentData,
    OsasYesOsaConclusionData,
    OsasYesOsaPolicyData,
    TherapielandProgramSelectionFields,
} from '@common/validation/customControls/osas/osasConsultSchemas/types';
export const osaYesConclusionInitialData: OsasYesOsaConclusionData = {
    additionalConclusionChoice: null,
    additionalConclusionText: null,
    alcoholStoppedSince: null,
    bmiCategory: null,
    comorbidities: null,
    comorbiditiesFromReferral: {
        COPD: false,
        burnoutOrDepressed: false,
        cardialProblems: false,
        diabetesMellitus: false,
        highBloodPressure: false,
        other: null,
    },
    comorbidityChoice: null,
    measureOfDiseaseBurden: null,
    measureOfOsa: null,
};
export const osaNoPolicyInitialData: OsasNoOsaPolicyData = {
    adviceGeneralPractitionerNoOsa: null,
    remarkAdviceGeneralPractitionerNoOsa: null,
    remarkNoOsa: null,
};
export const osaYesPolicyInitialData: OsasYesOsaPolicyData = {
    hasTreatment: null,
    isDrivingCompetenceDiscussed: null,
    remarkIsDrivingCompetenceDiscussed: null,
    remarkOther: null,
};

export const osasNoChosenTreatmentInitialData: OsasNoChosenTreatmentData = {
    callPatientAfterWeeks: null,
    messageToGeneralPractitioner: null,
    remarkNoTreatment: null,
    shouldNotifyGeneralPractitioner: null,
};

export const intoxicationCustomControlInitialData: IntoxicationCustomControlData = {
    alcoholStoppedSince: null,
    daysAWeek: null,
    drugUseStoppedSince: null,
    glassesADay: null,
    isAlcoholUser: null,
    isDrugUser: null,
    isDrugUserRemark: null,
    isSmoker: null,
    isSmokerRemark: null,
    packsADaySmoked: null,
    smokingStoppedSince: null,
    yearsSmoked: null,
};

export const osasYesChosenTreatmentInitialData: Omit<OsasYesChosenTreatmentData, 'taskForPoliclinic'> = {
    adviceGeneralPractitionerForOsa: null,
    allTreatments: [],
    cpapSetting: null,
    cpapSettingText: null,
    refer: {
        conclusion: null,
        department: null,
        message: null,
        organization: null,
        question: null,
        reason: null,
        specialismName: null,
        subject: null,
    },
    remarkTreatment: null,
    resendQuestionnaires: null,
    resendQuestionnairesWeeks: null,
    shouldHaveFollowUpConsult: null,
    shouldHaveFollowingExamination: null,
    taskFollowingExaminationOrConsultWeeks: null,
    treatment: {
        CPAP: false,
        MRA: false,
        SPT: false,
        lifestyle: false,
        other: false,
        refer: false,
    },
    treatmentFollowUp: {
        CPAP: [],
        MRA: [],
        SPT: [],
    },
};

export const therapielandProgramSelectionInitialData: TherapielandProgramSelectionFields = {
    checkedTreatments: [],
};

export const osasConsultExaminationInitialData: OsasConsultExaminationData = {
    T90Percent: null,
    centralSaGreaterThan10: null,
    examinationDate: null,
    otherRelevantFindings: {
        frequentMovements: false,
        indicationOfHeartRhythmDisorder: false,
        inefficientSleepHygiene: false,
        other: null,
        suboptimalSleepEfficiency: false,
    },
    positionDependent: null,
    relevantHypoxemia: null,
    remarkPositionDependent: null,
    remarkRelevantHypoxemia: null,
};

export const watchPatSleepStudyPdfInitialData: WatchPatSleepStudyPdfData = {
    AHICentral: null,
    odi3Percent: null,
    odi4Percent: null,
    oxygenSaturationMeanValue: null,
    oxygenSaturationMinValue: null,
    pAhi3Percent: null,
    satBelow90InSeconds: null,
    totalSleepTimeInSeconds: null,
};

export const initialData: OsasConsultData = {
    ...osaYesConclusionInitialData,
    ...osaNoPolicyInitialData,
    ...osaYesPolicyInitialData,
    ...osasNoChosenTreatmentInitialData,
    ...osasYesChosenTreatmentInitialData,
    ...therapielandProgramSelectionInitialData,
    ...osasConsultExaminationInitialData,
    ...watchPatSleepStudyPdfInitialData,
    ...intoxicationCustomControlInitialData,
    additionalAnamnesis: null,
    additionalPhysicalExamination: null,
    allTreatments: [],
    callPatientAfterWeeks: null,
    consultDate: DateTime.now().format('YYYY-MM-DD'),
    hasOsa: null,
    hasOsaOtherText: null,
    taskForPoliclinic: null,
};
